import React, { useEffect } from 'react'
import { useRecoilValue } from 'recoil'

import RefundSelect from '@/features/support/refund/components/RefundSelect'
import RefundTerms from '@/features/support/refund/components/RefundTerm'
import {
  RefundAccount,
  RefundName,
  RefundTermYN,
  RefundTypeState,
  useRefundResetCallback,
} from '@/features/support/refund/store'
import InputForm from '@/shared/components/common/CommonForm/InputForm'
import Loading from '@/shared/components/common/Loading'
import DoubleModal from '@/shared/components/common/Modal/DoubleModal'
import Modal from '@/shared/components/common/Modal/Modal'
import TelChoiceTab from '@/shared/components/common/Subscript/TelTab'
import { COLOR } from '@/shared/constant/color'
import useWindowDimensions from '@/shared/hooks/useGetWindowSize'
import useOnInput from '@/shared/hooks/useOnInput'
import useSubscriptQuery from '@/shared/hooks/useSubscriptQuery'
import useValid from '@/shared/hooks/useValid'
import { CertNumState, DouModalOption, TelNumState, TelTypeState } from '@/shared/store'
import { InputDataListType } from '@/shared/types/type'
import { validInputLength } from '@/shared/utils'
import { CustomBtn } from '@/styles/common/CommonForm/buttonform'
import { SubScriptContent, SubScriptWrap } from '@/styles/common/subscript'
import {
  RefundCaution,
  RefundSktNotice,
  RefundSktNoticeRed,
  RefundWrap,
  SubScriptDesc,
  SubScriptTitle,
  TelChoiceTabTitle,
} from '@/styles/views/Support/refund'

const RefundPage = () => {
  const { width } = useWindowDimensions()
  const valid = useValid()
  const onInput = useOnInput()

  const TelNum = useRecoilValue(TelNumState)
  const CertNum = useRecoilValue(CertNumState)
  const RefundType = useRecoilValue(RefundTypeState)
  const RefundTermYNState = useRecoilValue(RefundTermYN)
  const RefundNameState = useRecoilValue(RefundName)
  const RefundAccountState = useRecoilValue(RefundAccount)
  const DouModalSetting = useRecoilValue(DouModalOption)
  const TelType = useRecoilValue(TelTypeState)
  const isSkt = TelType === 0

  const resetCallback = useRefundResetCallback()

  const {
    securityCheck,
    refund,
    openRefundModal,
    openAlertModal,
    openConfirmModal,
    handleDevideSktCancel,
    handleDevideSktConfirm,
    handleAlertReloadConfirm,
    RefundIsLoading,
    douIsOpen,
    modalSetting,
    isOpen,
    douToggle,
    toggle,
  } = useSubscriptQuery()

  const SubmitCondition =
    TelNum[0].check &&
    !validInputLength(CertNum, 4) &&
    RefundType !== 'none' &&
    RefundAccountState[0].check &&
    RefundNameState[0].check &&
    RefundTermYNState

  const InputDataList: InputDataListType[] = [
    {
      title: '계좌번호 입력',
      valid: valid.RefundAccountValid,
      check: RefundAccountState[0].check,
      oninput: onInput.RefundAccount,
      placeholder: '계좌번호를 입력해주세요.',
      errMsg: '계좌번호를 정확히 입력해주세요.',
      blurFunc: valid.onBlurForValidationAccount,
    },
    {
      title: '예금주명 입력',
      valid: valid.RefundNameValid,
      check: RefundNameState[0].check,
      oninput: onInput.InquiryName,
      placeholder: '예금주명을 입력해주세요.',
      errMsg: '이름을 정확히 입력해주세요.',
      blurFunc: valid.onBlurForValidationString,
    },
  ]

  useEffect(() => {
    return () => {
      resetCallback()
    }
  }, [resetCallback])

  return (
    <RefundWrap>
      {RefundIsLoading && <Loading />}
      <SubScriptWrap>
        <SubScriptTitle>서비스 환불을 신청하기 전에 아래 내용을 꼭 확인해 주세요.</SubScriptTitle>
        <SubScriptDesc>
          <span className="warning">* 서비스 이용 이력이 없을 경우 환불이 가능합니다.</span>
        </SubScriptDesc>
        <SubScriptContent>
          <TelChoiceTabTitle>통신사 선택</TelChoiceTabTitle>
          <TelChoiceTab />
          <InputForm
            title="휴대폰 번호"
            valid={valid.PhoneNumValid}
            check={TelNum[0].check}
            placeholder="‘-’을 제외하고 입력해주세요."
            ClickFunc={securityCheck}
            oninput={onInput.PhoneNum}
          />
          <InputForm
            title="인증번호 입력"
            value={CertNum}
            valid={valid.CertNumValid}
            oninput={onInput.CertNum}
            placeholder="인증번호를 입력해주세요."
            blurFunc={valid.onBlurForValidationCert}
            errMsg="인증문자 입력을 확인해주세요."
          />

          <RefundSelect title="환불 받을 은행 선택" />
          {InputDataList?.map((element: InputDataListType) => (
            <InputForm
              title={element.title}
              value={element.value}
              valid={element.valid}
              check={element.check}
              oninput={element.oninput}
              placeholder={element.placeholder}
              key={element.title}
              blurFunc={element.blurFunc}
              errMsg={element.errMsg}
            />
          ))}
        </SubScriptContent>
      </SubScriptWrap>
      <RefundTerms />
      {isSkt && (
        <RefundSktNotice>
          ※ 환불 접수 시 운영정책에 따라 해당 번호의 서비스 재가입이 일정 기간 제한될 수 있습니다.
        </RefundSktNotice>
      )}
      <CustomBtn
        color={COLOR.white}
        bgColor={SubmitCondition ? COLOR.point1 : COLOR.gray30}
        borderRadius={'40px'}
        marginTop={width > 1200 ? '64px' : width < 361 ? '36px' : '56px'}
        width={width > 360 ? '356px' : '328px'}
        height={width > 1200 ? '56px' : '44px'}
        fontSize={width > 1200 ? '18px' : '14px'}
        fontFamily={width > 1200 ? 'NanumSquareNeoHeavy, sans-serif' : 'NotoSansBold, sans-serif'}
        onClick={refund}
      >
        <span>{isSkt ? '환불 접수' : '서비스 해지 후 환불 등록'}</span>
      </CustomBtn>
      <RefundCaution>
        <RefundSktNoticeRed>
          * 가입 당일 해지하신 고객님은 요금이 청구되지 않습니다.
        </RefundSktNoticeRed>
        <RefundSktNoticeRed>
          * 서비스 이용요금은 매월 사용일 수 만큼 계산되어 청구됩니다.
        </RefundSktNoticeRed>
        <p>
          * 데이터유니버스는 고객의 환불 요청에 따른 조치를 이행하기 위하여 개인정보보호법 제 15조
          제1항 4호에 근거하여 개인정보를 수집 이용합니다. (환불처리 완료 후 3년간까지)
        </p>
      </RefundCaution>
      <Modal
        isOpen={isOpen}
        type={modalSetting.type}
        toggle={toggle}
        doubleToggle={douToggle}
        setOption={modalSetting.setOption}
        option={modalSetting.option}
        ClickFunc={toggle}
        ClickFunc2={douToggle}
        ClickFunc3={openRefundModal}
        ClickFunc4={openAlertModal}
        ClickFunc5={openConfirmModal}
        handleDevideSktCancel={handleDevideSktCancel}
        handleDevideSktConfirm={handleDevideSktConfirm}
        handleAlertReloadConfirm={handleAlertReloadConfirm}
      />
      <DoubleModal
        isOpen={douIsOpen}
        toggle={toggle}
        toggle2={douToggle}
        option={DouModalSetting}
      />
    </RefundWrap>
  )
}
export default RefundPage
